/**
 * Google Places API service
 * Reusable methods for Places API (New) web service calls
 */

const API_BASE_URL = "https://places.googleapis.com/v1";

/**
 * Gets the Google Maps API key from environment variables
 * @returns {string} API key
 * @throws {Error} If API key is not configured
 */
function getApiKey() {
  const apiKey = process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY;
  if (!apiKey) {
    throw new Error("Google Maps API key is not configured");
  }
  return apiKey;
}

/**
 * Generates a session token for billing optimization
 * @returns {string} Session token
 */
export function generateSessionToken() {
  return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Searches for places using Google Places Autocomplete API
 * @param {Object} options - Search options
 * @param {string} options.input - Search query string
 * @param {string} [options.sessionToken] - Session token for billing (optional)
 * @param {string[]} [options.includedRegionCodes] - Region codes to restrict search (default: ["in"])
 * @param {string[]} [options.includedPrimaryTypes] - Primary types to include (default: ["geocode"])
 * @param {string} [options.languageCode] - Language code (default: "en")
 * @returns {Promise<Array>} Array of place predictions
 * @throws {Error} If API call fails
 */
export async function searchPlacesAutocomplete({
  input,
  sessionToken = null,
  includedRegionCodes = ["in"],
  includedPrimaryTypes = ["geocode"],
  languageCode = "en",
}) {
  if (!input || !input.trim()) {
    return [];
  }

  const apiKey = getApiKey();
  const url = `${API_BASE_URL}/places:autocomplete`;

  const requestBody = {
    input: input.trim(),
    includedRegionCodes,
    includedPrimaryTypes,
    languageCode,
    ...(sessionToken && { sessionToken }),
  };

  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Goog-Api-Key": apiKey,
      "X-Goog-FieldMask":
        "suggestions.placePrediction.placeId,suggestions.placePrediction.text",
    },
    body: JSON.stringify(requestBody),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.error?.message ||
        `HTTP ${response.status}: ${response.statusText}`
    );
  }

  const data = await response.json();

  return (
    data.suggestions
      ?.filter((suggestion) => suggestion.placePrediction)
      .map((suggestion) => suggestion.placePrediction) || []
  );
}

/**
 * Fetches place details from Google Places API
 * @param {Object} options - Place details options
 * @param {string} options.placeId - Google Places place_id
 * @param {string} [options.sessionToken] - Session token for billing (optional)
 * @param {string} [options.fieldMask] - Field mask for requested fields (default: "addressComponents,formattedAddress")
 * @param {AbortSignal} [options.signal] - Abort signal for cancelling the request
 * @returns {Promise<Object>} Place details
 * @throws {Error} If API call fails
 */
export async function fetchPlaceDetails({
  placeId,
  sessionToken = null,
  fieldMask = "addressComponents,formattedAddress",
  signal = null,
}) {
  if (!placeId) {
    throw new Error("placeId is required");
  }

  const apiKey = getApiKey();
  const cleanPlaceId = placeId.replace(/^places\//, "");
  const url = `${API_BASE_URL}/places/${cleanPlaceId}`;

  const headers = {
    "Content-Type": "application/json",
    "X-Goog-Api-Key": apiKey,
    "X-Goog-FieldMask": fieldMask,
    ...(sessionToken && { "X-Goog-Session-Token": sessionToken }),
  };

  const response = await fetch(url, {
    method: "GET",
    headers,
    signal,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const error = new Error(
      errorData.error?.message ||
        `HTTP ${response.status}: ${response.statusText}`
    );
    // Preserve abort signal error
    if (signal?.aborted) {
      error.message = "Request aborted";
    }
    throw error;
  }

  return await response.json();
}
